/* eslint-disable no-irregular-whitespace */
import { useState, useEffect } from 'react';
import { Button, Card, CardContent, Typography, Stack, CircularProgress } from '@mui/material';
import { TARGET_KEYS, TargetKey } from 'src/types/targets-constants';
import { TargetData, GeneralTargetData, StockOptimizationData } from 'src/types/targets';
import axiosInstance from 'src/utils/axios';
import { useLocales } from 'src/locales';
import { PageContainer } from 'src/components/page-container';
import TargetsModal from './targets-modal';
import CreateTarget from './target-add-edit-form';

export default function TargetsPage() {
  const [openModal, setOpenModal] = useState<TargetKey | null>(null);
  const [targets, setTargets] = useState<Record<string, TargetData | null>>({});
  const [loading, setLoading] = useState(true);

  const { t } = useLocales('targetsPage');

  const handleOpen = (key: TargetKey) => setOpenModal(key);
  const handleClose = () => setOpenModal(null);

  const [stockOptimizationOpen, setStockOptimizationOpen] = useState(false);

  const openStockOptimizationModal = () => setStockOptimizationOpen(true);
  const closeStockOptimizationModal = () => setStockOptimizationOpen(false);

  const fetchTargets = async () => {
    setLoading(true);
    try {
      const targetKeys = Object.values(TARGET_KEYS);

      const fetchPromises = targetKeys.map((key) => {
        let apiCall;

        if (key === TARGET_KEYS.OTTIMIZZAZIONE_STOCK) {
          apiCall = axiosInstance.get('/admin/dashboard/optimization-target');
        } else {
          apiCall = axiosInstance.get(`/admin/targets/${key}`);
        }

        return apiCall.catch((error) => {
          console.error(`Failed to fetch target for key: ${key}`, error);
          return { data: { data: null } };
        });
      });

      const responses = await Promise.all(fetchPromises);

      const mappedTargets = responses.reduce(
        (acc: Record<string, TargetData>, res, index) => {
          const currentTarget = res.data.data;
          const key = targetKeys[index];

          if (currentTarget && key) {
            acc[key] = {
              ...currentTarget,
              key,
              euroTarget: currentTarget.euroTarget
                ? parseFloat(currentTarget.euroTarget)
                : undefined,
            };
          }
          return acc;
        },
        {} as Record<string, TargetData>,
      );

      setTargets(mappedTargets);
    } catch (error) {
      console.error('Failed to fetch targets', error);
      setTargets({});
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTargets();
  }, []);

  const handleTargetUpdated = (key: TargetKey, updated: TargetData) => {
    setTargets((prev) => ({
      ...prev,
      [key]: updated,
    }));
  };

  const renderTargetInfo = (key: TargetKey) => {
    const data = targets[key];
    if (!data)
      return (
        <Typography
          variant="body2"
          sx={{
            color: 'text.secondary',
          }}
        >
          {t('no_target_set')}
        </Typography>
      );

    switch (key) {
      case TARGET_KEYS.SITUAZIONE_GIORNALIE:
      case TARGET_KEYS.SITUAZIONE_SETTIMANALE: {
        const generalData = data as GeneralTargetData;
        if (generalData.targetBottle !== undefined && generalData.euroTarget !== undefined) {
          return (
            <Typography
              variant="body2"
              sx={{
                color: 'text.secondary',
              }}
            >
              {t('bottles')}: {generalData.targetBottle}% | {t('euro')}: {generalData.euroTarget}%
            </Typography>
          );
        }
        break;
      }

      case TARGET_KEYS.OTTIMIZZAZIONE_STOCK: {
        const stockData = data as StockOptimizationData;
        if (
          stockData.targetDIO !== undefined &&
          stockData.targetReductionRate !== undefined &&
          stockData.startDate !== undefined &&
          stockData.targetDate !== undefined
        ) {
          return (
            <Typography
              variant="body2"
              sx={{
                color: 'text.secondary',
              }}
            >
              {t('dio')}: {stockData.targetDIO}| {t('reduction_rate')}:{' '}
              {stockData.targetReductionRate}| {t('start_date')}: {stockData.startDate}|{' '}
              {t('end_date')}: {stockData.targetDate}
            </Typography>
          );
        }
        break;
      }

      default:
        break;
    }
    return (
      <Typography
        variant="body2"
        sx={{
          color: 'text.secondary',
        }}
      >
        {t('no_target_set')}
      </Typography>
    );
  };

  if (loading) {
    return (
      <Stack
        sx={{
          alignItems: 'center',
          justifyContent: 'center',
          mt: 4,
        }}
      >
        <CircularProgress />
      </Stack>
    );
  }

  return (
    <PageContainer variant="data">
      <Stack spacing={3}>
        {/* Situazione Giornaliera */}
        <Card>
          <CardContent>
            <Stack
              direction="row"
              sx={{
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <Stack>
                <Typography variant="h6">{t('situazione_giornaliera')}</Typography>
                {renderTargetInfo(TARGET_KEYS.SITUAZIONE_GIORNALIE)}
              </Stack>
              <Button
                variant="contained"
                onClick={() => handleOpen(TARGET_KEYS.SITUAZIONE_GIORNALIE)}
              >
                {t('set_target')}
              </Button>
            </Stack>
          </CardContent>
        </Card>

        {/* Situazione Settimanale */}
        <Card>
          <CardContent>
            <Stack
              direction="row"
              sx={{
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <Stack>
                <Typography variant="h6">{t('situazione_settimanale')}</Typography>
                {renderTargetInfo(TARGET_KEYS.SITUAZIONE_SETTIMANALE)}
              </Stack>
              <Button
                variant="contained"
                onClick={() => handleOpen(TARGET_KEYS.SITUAZIONE_SETTIMANALE)}
              >
                {t('set_target')}
              </Button>
            </Stack>
          </CardContent>
        </Card>

        {/* Ottimizzazione Stock */}
        <Card>
          <CardContent>
            <Stack
              direction="row"
              sx={{
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <Stack>
                <Typography variant="h6">{t('ottimizzazione_stock')}</Typography>
                {renderTargetInfo(TARGET_KEYS.OTTIMIZZAZIONE_STOCK)}
              </Stack>
              <Button variant="contained" onClick={openStockOptimizationModal}>
                {t('set_target')}
              </Button>
            </Stack>
          </CardContent>
        </Card>

        {/* Stock Optimization Modal */}
        <CreateTarget
          dialog={{ value: stockOptimizationOpen, onFalse: closeStockOptimizationModal }}
          onTargetUpdated={fetchTargets}
        />

        {/* Targets Modal */}
        {openModal && (
          <TargetsModal
            open={Boolean(openModal)}
            onClose={handleClose}
            targetKey={openModal}
            onTargetUpdated={handleTargetUpdated}
          />
        )}
      </Stack>
    </PageContainer>
  );
}
